import { useEffect, useState } from 'react'
import {
  RefreshCw, Users, ChevronDown, ChevronUp,
  Loader2, Search, Briefcase, Mail, Phone, MapPin,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import AdminLayout from '@/components/admin/AdminLayout'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })
}

const BOOKING_STATUS_COLOR = {
  pending:          'text-yellow-400 bg-yellow-500/10 border-yellow-500/25',
  confirmed:        'text-blue-400 bg-blue-500/10 border-blue-500/25',
  assigned:         'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25',
  en_route:         'text-purple-400 bg-purple-500/10 border-purple-500/25',
  in_progress:      'text-orange-400 bg-orange-500/10 border-orange-500/25',
  pending_approval: 'text-pink-400 bg-pink-500/10 border-pink-500/25',
  completed:        'text-green-400 bg-green-500/10 border-green-500/25',
}

// ── Customer Row ──────────────────────────────────────────────────────────────

function CustomerRow({ customer }) {
  const [expanded, setExpanded] = useState(false)
  const [bookings, setBookings] = useState(null) // null = not loaded

  async function loadBookings() {
    if (bookings !== null) return
    const { data } = await supabase
      .from('bookings')
      .select('id, ref_number, status, address, created_at, service_categories(name)')
      .eq('customer_id', customer.id)
      .order('created_at', { ascending: false })
      .limit(10)
    setBookings(data ?? [])
  }

  function handleExpand() {
    setExpanded((v) => {
      if (!v) loadBookings()
      return !v
    })
  }

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl overflow-hidden">
      {/* Summary row */}
      <button
        onClick={handleExpand}
        className="w-full px-4 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-surface-overlay transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <p className="text-sm font-semibold text-ink-primary">{customer.full_name ?? 'Unknown'}</p>
            {customer._bookingCount > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full border font-medium text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25">
                {customer._bookingCount} booking{customer._bookingCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-ink-muted">
            {customer.email   && <span className="flex items-center gap-1"><Mail    className="w-3 h-3" />{customer.email}</span>}
            {customer.phone   && <span className="flex items-center gap-1"><Phone   className="w-3 h-3" />{customer.phone}</span>}
            {customer.city    && <span className="flex items-center gap-1"><MapPin  className="w-3 h-3" />{customer.city}</span>}
          </div>
          <p className="text-xs text-ink-muted mt-0.5">Joined {fmtDate(customer.created_at)}</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-ink-muted shrink-0" /> : <ChevronDown className="w-4 h-4 text-ink-muted shrink-0" />}
      </button>

      {/* Expanded — booking list */}
      {expanded && (
        <div className="border-t border-surface-border px-4 py-4">
          {bookings === null ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 text-siglat-teal animate-spin" />
            </div>
          ) : bookings.length === 0 ? (
            <p className="text-xs text-ink-muted italic text-center py-2">No bookings yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {bookings.map((bk) => {
                const color = BOOKING_STATUS_COLOR[bk.status] ?? 'text-ink-muted bg-surface-overlay border-surface-border'
                const city  = (bk.address ?? '').split(',').pop()?.trim() ?? ''
                return (
                  <div key={bk.id} className="flex items-start justify-between gap-3 bg-surface-overlay rounded-lg px-3 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <p className="text-xs font-mono text-ink-secondary">{bk.ref_number ?? bk.id.slice(0, 8).toUpperCase()}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${color}`}>
                          {bk.status}
                        </span>
                      </div>
                      <p className="text-xs text-ink-muted">
                        {bk.service_categories?.name ?? '—'}
                        {city ? ` · ${city}` : ''}
                      </p>
                    </div>
                    <p className="text-[10px] text-ink-muted shrink-0 mt-0.5">{fmtDate(bk.created_at)}</p>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminCustomers() {
  const [customers,  setCustomers]  = useState([])
  const [filtered,   setFiltered]   = useState([])
  const [loading,    setLoading]    = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [search,     setSearch]     = useState('')

  async function fetchCustomers() {
    setLoading(true)

    // All customer profiles
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name, email, phone, city, created_at')
      .eq('role', 'customer')
      .order('created_at', { ascending: false })

    if (!profiles?.length) { setCustomers([]); setFiltered([]); setLoading(false); return }

    // Booking counts per customer (count via bookings table)
    const ids = profiles.map((p) => p.id)
    const { data: bookingRows } = await supabase
      .from('bookings')
      .select('customer_id')
      .in('customer_id', ids)

    const countMap = {}
    for (const b of bookingRows ?? []) {
      countMap[b.customer_id] = (countMap[b.customer_id] ?? 0) + 1
    }

    const enriched = profiles.map((p) => ({
      ...p,
      _bookingCount: countMap[p.id] ?? 0,
    }))

    setCustomers(enriched)
    setFiltered(enriched)
    setLoading(false)
  }

  useEffect(() => { fetchCustomers() }, []) // eslint-disable-line

  // Search filter
  useEffect(() => {
    if (!search.trim()) { setFiltered(customers); return }
    const q = search.toLowerCase()
    setFiltered(
      customers.filter((c) =>
        (c.full_name ?? '').toLowerCase().includes(q) ||
        (c.email     ?? '').toLowerCase().includes(q) ||
        (c.phone     ?? '').toLowerCase().includes(q) ||
        (c.city      ?? '').toLowerCase().includes(q)
      )
    )
  }, [search, customers])

  async function handleRefresh() {
    setRefreshing(true)
    await fetchCustomers()
    setRefreshing(false)
  }

  const withBookings = customers.filter((c) => c._bookingCount > 0).length

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">Customers</h1>
            <p className="text-sm text-ink-muted mt-0.5">
              {customers.length} registered ·{' '}
              <span className="text-siglat-teal font-medium">{withBookings} with bookings</span>
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-ink-muted hover:text-ink-primary hover:bg-surface-overlay rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, city…"
            className="w-full bg-surface-raised border border-surface-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-siglat-teal transition-colors"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="h-20 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-10 text-center">
            <Users className="w-8 h-8 text-ink-muted mx-auto mb-2 opacity-30" />
            <p className="text-sm text-ink-muted">
              {search ? 'No customers match your search.' : 'No customers yet.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {filtered.map((c) => (
              <CustomerRow key={c.id} customer={c} />
            ))}
          </div>
        )}

      </div>
    </AdminLayout>
  )
}
